import { Button } from '@HoudiniUI/Button';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { toast } from 'sonner';
import { FindingCard } from './FindingCard';
import { FindingGroup } from './FindingGroup';
import { buildFixesClipboardText, getSourceLabel, groupFindingsBySource, pluralize } from './complianceDerived';
import { styleSheet } from './styles';

export const FindingsSection = ({
	blockers,
	warnings,
	label,
	collapseBlockers,
}: {
	blockers: Api.CampaignComplianceFinding[];
	warnings: Api.CampaignComplianceFinding[];
	label: string;
	collapseBlockers: boolean;
}) => {
	if (!blockers.length && !warnings.length) {
		return null;
	}
	const showCopyAll = blockers.length > 0 && !collapseBlockers;
	const onCopyAll = async () => {
		try {
			await navigator.clipboard.writeText(buildFixesClipboardText(blockers));
			toast.success(`All ${blockers.length} fixes copied, ready to send to the client.`);
		} catch {
			toast.error('The fixes could not be copied to the clipboard.');
		}
	};
	return (
		<section className={css(styleSheet.section)}>
			<div className={css(styleSheet.sectionHead)}>
				<h3 className={css(styleSheet.sectionTitle)}>{label}</h3>
				{showCopyAll ? (
					<Button variant='outline' size='sm' onClick={onCopyAll}>
						Copy all fixes for the client
					</Button>
				) : (
					<span className={css(styleSheet.sectionCount)}>
						{blockers.length ? pluralize(blockers.length, 'blocker') : 'advisory only'}
					</span>
				)}
			</div>
			{collapseBlockers && blockers.length ? (
				<FindingGroup
					label={pluralize(blockers.length, 'blocker')}
					count='— released without being fixed, expand to view'
				>
					{blockers.map(finding => (
						<FindingCard key={finding.code} finding={finding} />
					))}
				</FindingGroup>
			) : (
				groupFindingsBySource(blockers).map(group => (
					<FindingGroup
						key={group.source}
						label={getSourceLabel(group.source)}
						count={`(${group.findings.length})`}
						defaultOpen
					>
						{group.findings.map(finding => (
							<FindingCard key={finding.code} finding={finding} />
						))}
					</FindingGroup>
				))
			)}
			{warnings.length ? (
				<FindingGroup label='Warnings' count={`(${warnings.length}) — advisory, do not block`}>
					{warnings.map(finding => (
						<FindingCard key={finding.code} finding={finding} />
					))}
				</FindingGroup>
			) : null}
		</section>
	);
};
